import type { ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import Big from 'big.js';
import { InlineFormattedNumber } from '../../../FormattedNumber';
import { Text } from '../../../Text';
import { baseColumn } from '../baseColumn';
import type { ColDefFactory, Column } from '../types';
import { numericColumnComparator } from '../utils';
import { getOtcStrikeFwdPriceColumnValue, type OtcStrikeFwdPriceRow } from './getOtcStrikeFwdPriceColumnValue';
import type { OtcStrikeFwdPriceParams, OtcStrikeFwdPriceValue } from './otcStrikeFwdPrice.types';

function otcStrikeFwdPriceComparator(a: OtcStrikeFwdPriceValue | undefined, b: OtcStrikeFwdPriceValue | undefined) {
  if (a == null && b == null) {
    return 0;
  }
  if (a == null) {
    return -1;
  }
  if (b == null) {
    return 1;
  }
  if (a.type === 'text' && b.type === 'text') {
    return (a.value ?? '').localeCompare(b.value ?? '');
  }
  if (a.type === 'text') {
    return -1;
  }
  if (b.type === 'text') {
    return 1;
  }
  return numericColumnComparator(a.value, b.value);
}

export const otcStrikeFwdPrice: ColDefFactory<Column<OtcStrikeFwdPriceParams>> = column => ({
  initialWidth: 150,
  minWidth: 70,
  maxWidth: 210,
  ...baseColumn(column),
  type: 'numericColumn',
  headerClass: () => {
    const columnGroupClass = column.columnGroup ? 'ag-custom-column-group' : '';
    return column.params?.align !== 'left' ? `ag-right-aligned-header ${columnGroupClass}` : `${columnGroupClass}`;
  },
  cellClass: () => {
    return column.params?.align !== 'left' ? 'ag-right-aligned-cell' : '';
  },
  valueGetter: (params: ValueGetterParams): OtcStrikeFwdPriceValue | undefined =>
    getOtcStrikeFwdPriceColumnValue(params as ValueGetterParams<OtcStrikeFwdPriceRow>),
  cellRenderer: ({ value, context }: ICellRendererParams<unknown, OtcStrikeFwdPriceValue>) => {
    if (value == null) {
      return <></>;
    }
    if (value.type === 'text') {
      if (value.value == null || value.value === '') {
        return <></>;
      }
      return <Text>{value.value}</Text>;
    }
    if (value.value == null || value.value === '') {
      return <></>;
    }
    const currencyInfo = value.currency ? context.current.currenciesBySymbol?.get(value.currency) : undefined;
    const increment = value.increment ?? currencyInfo?.DefaultIncrement;
    return (
      <InlineFormattedNumber
        number={value.value}
        currency={value.currency}
        increment={increment}
        background="backgroundBlotterTableRowHover"
      />
    );
  },
  valueFormatter: ({ value }: ValueFormatterParams<unknown, OtcStrikeFwdPriceValue>): string => {
    if (value == null) {
      return '';
    }
    if (value.type === 'text') {
      return value.value ?? '';
    }
    if (value.value == null || value.value === '') {
      return '';
    }
    try {
      return Big(value.value).toFixed();
    } catch {
      return value.value;
    }
  },
  comparator: otcStrikeFwdPriceComparator,
});
